import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectGroupById,
  getCurrentIdentityGroups,
  getCurrentIdentityGroupsRecursively,
  searchForGroupExternal,
} from "../../actions/api";
import { Typography, Row, Col, Card, Space, Spin } from "antd";
import { groupsUserRoleIdentifier } from "../../config";
import { types as at, errorAction } from "@authzsvc/api-lib";
import { GroupSyncTypes } from "../../constants/enums";
import { userTokenHasRole } from "../../selectors";
import { DEFAULT_GROUP_TABLE_QUERY_FIELDS } from "../../constants";
import SubscriptionButton from "../group/SubscriptionButton";
import { showErrorNotification } from "../../util";

const { Title, Paragraph } = Typography;

const GroupSubscriptionLink = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  const groupTableQueryFields = [
    ...DEFAULT_GROUP_TABLE_QUERY_FIELDS,
    "selfSubscriptionType",
    "approvalRequired",
    "administratorsId",
  ];

  const isExternal = useSelector(
    (state) => !userTokenHasRole(state, "group_reader")
  );

  const externalGroupSearch = useSelector(
    (state) => state.groups.groupSearch?.[0]
  );

  const groupDirectMembership = useSelector(
    (state) => state?.groupMembership.direct
  );
  const selectedGroup = useSelector((state) => state.groups.selectedGroup);
  const userRoles = useSelector(
    (state) => state?.keycloak.instance.tokenParsed.cern_roles
  );

  const isMember = useMemo(
    () => groupDirectMembership?.some((m) => m.groupIdentifier === id),
    [groupDirectMembership, id]
  );

  const isCernUser = useMemo(
    () => userRoles?.includes(groupsUserRoleIdentifier),
    [userRoles]
  );

  const groupData = isExternal ? externalGroupSearch : selectedGroup;

  const init = async () => {
    setLoading(true);
    if (isExternal) {
      await dispatch(
        searchForGroupExternal({
          filter: `groupIdentifier:Eq:${id}`,
          sort: "groupIdentifier",
          limit: 1,
          field: groupTableQueryFields,
        })
      );
    } else {
      const [, , result] = await Promise.all([
        dispatch(getCurrentIdentityGroups()),
        dispatch(getCurrentIdentityGroupsRecursively()),
        dispatch(selectGroupById(id)),
      ]);

      if (result.type === errorAction(at.GET_GROUP_BY_ID)) {
        showErrorNotification(
          result?.payload?.title || "Failed to find group",
          result?.payload?.data?.message || "Sorry, something went wrong"
        );
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const groupIsSlave = () =>
    groupData &&
    groupData?.syncType !== GroupSyncTypes.Master &&
    groupData?.syncType !== GroupSyncTypes.NoSync;

  const getSubscriptionProperties = () => {
    if (!groupData || Object.keys(groupData).length === 0) {
      return {
        title: "Group Not Found",
        description: `Group ${id} could not be found. Please check the link and try again.`,
        disabled: true,
      };
    }

    if (groupData?.dynamic) {
      return {
        title: "Subscription Disabled",
        description:
          "This is a dynamic group. You cannot subscribe to a dynamic group as membership is calculated automatically.",
        disabled: true,
      };
    }

    if (isMember) {
      return {
        title: "You're Already Subscribed",
        description: `You are already a member of ${id}. No further action is needed.`,
        disabled:
          groupData?.selfSubscriptionType === "Closed" ||
          groupIsSlave() ||
          (groupData?.selfSubscriptionType === "CernUsers" && !isCernUser),
      };
    }

    if (groupIsSlave()) {
      return {
        title: "Subscription Disabled",
        description:
          "This group is managed by Egroups. You cannot subscribe through GMS.",
        disabled: true,
      };
    }

    if (groupData?.selfSubscriptionType === "Closed") {
      return {
        title: "Subscription Disabled",
        description:
          "Self-subscription to this group is closed. Only administrators can manage its membership.",
        disabled: true,
      };
    }

    if (groupData?.selfSubscriptionType === "CernUsers" && !isCernUser) {
      return {
        title: "Subscription Disabled",
        description:
          "Self-subscription to this group is only possible for CERN users.",
        disabled: true,
      };
    }

    return {
      title: "You're Almost There",
      description: `You've accessed this page via a direct group invitation link. Click below to confirm your subscription and join ${id}.`,
      disabled: false,
    };
  };

  const { title, description, disabled } = getSubscriptionProperties();

  return (
    <Spin spinning={loading}>
      {!loading && (
        <Row justify="center" style={{ minHeight: "100vh", paddingTop: 100 }}>
          <Col xs={22} sm={16} md={12} lg={10} xl={8}>
            <Card
              style={{
                padding: 24,
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                textAlign: "center",
              }}
            >
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Title level={3} style={{ marginBottom: 0 }}>
                  {title}
                </Title>
                <Paragraph style={{ marginBottom: 0 }}>{description}</Paragraph>

                <SubscriptionButton
                  group={groupData}
                  disabled={disabled || !groupData}
                  tooltipMessage={description}
                />
              </Space>
            </Card>
          </Col>
        </Row>
      )}
    </Spin>
  );
};

export default GroupSubscriptionLink;
